/**
 * Turns the server's stage CODE into the words the waiting indicator shows.
 *
 * The header used to render the server's `routing` text directly. That event
 * carries two unrelated things under one name: short stage announcements, and
 * long retrospective narration — much of it reporting failures the build is
 * already recovering from ("could not create master screen X — skipping the app
 * shell"). Putting that in front of a user during a healthy build was wrong.
 *
 * So the server now sends a code and numbers, and every word below belongs to
 * the client. Narration keeps its own `message` field and is ignored, exactly as
 * it was before this feature existed — it cannot leak into the header by anyone
 * forgetting to opt out, because there is nothing to opt out of.
 *
 * The only server-supplied text that reaches the screen is the user's own screen
 * name on `designing_screen`.
 */

/** Mirror of the server's `ChatPhaseCode`. Adding a member without wording below is a compile error. */
export type ChatPhaseCode =
  | "reading_request"
  | "gathering_requirements"
  | "scoping"
  | "planning_backend"
  | "naming_data"
  | "planning_pages"
  | "checking_plan"
  | "planning_build"
  | "backend_tables"
  | "backend_actions"
  | "backend_tables_repair"
  | "backend_actions_repair"
  | "declaring_relations"
  | "designing_screen"
  | "connecting_pages"
  | "building_navigation"
  | "seeding_data"
  | "checking_app"
  | "wrapping_up";

/**
 * Base wording per stage. A `Record` over the closed union on purpose: a new
 * code cannot ship without a phrase.
 *
 * Kept short — this line sits above a 360px-wide panel and its job is to say
 * where the build is, not to explain what happened.
 */
const PHASE_LABEL: Record<ChatPhaseCode, string> = {
  reading_request: "Reading your request",
  gathering_requirements: "Working out the requirements",
  scoping: "Working out what to build",
  planning_backend: "Planning the backend",
  naming_data: "Working out the data",
  planning_pages: "Planning pages",
  checking_plan: "Checking the plan",
  planning_build: "Planning the build",
  backend_tables: "Setting up tables",
  backend_actions: "Adding actions",
  backend_tables_repair: "Fixing tables",
  backend_actions_repair: "Fixing actions",
  declaring_relations: "Working out table relations",
  designing_screen: "Designing",
  connecting_pages: "Connecting the pages",
  building_navigation: "Building the navigation",
  seeding_data: "Adding sample data",
  checking_app: "Checking the app",
  wrapping_up: "Wrapping up"
};

/** Shown when the server names a screen stage without a screen name. */
const UNNAMED_SCREEN: string = "the screen";

function isPhaseCode(value: unknown): value is ChatPhaseCode {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(PHASE_LABEL, value);
}

function readNumber(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null;
  return value;
}

/**
 * "(2 of 5)" — omitted when the position is unknown, nonsensical, or there is
 * only one of the thing. "(1 of 1)" tells the reader nothing and crowds the row.
 */
function positionSuffix(index: unknown, total: unknown): string {
  const at: number | null = readNumber(index);
  const of: number | null = readNumber(total);
  if (at === null || of === null) return "";
  if (of <= 1 || at > of) return "";
  return ` (${at} of ${of})`;
}

/**
 * Compose the header line from a `routing` event's payload.
 *
 * @returns null when the payload names no known stage — narration, an
 * unrecognised code from a newer server, or anything malformed off the wire.
 * Callers treat null as "leave the current stage standing".
 */
export function phaseLabelFromEvent(data: unknown): string | null {
  if (typeof data !== "object" || data === null) return null;
  const payload = data as { phase?: unknown; index?: unknown; total?: unknown; name?: unknown };
  if (!isPhaseCode(payload.phase)) return null;

  const base: string = PHASE_LABEL[payload.phase];

  if (payload.phase === "designing_screen") {
    const name: string = typeof payload.name === "string" ? payload.name.trim() : "";
    const where: string = name.length > 0 ? name : UNNAMED_SCREEN;
    return `${base} ${where}${positionSuffix(payload.index, payload.total)}`;
  }

  if (payload.phase === "checking_plan") {
    // Repair rounds have a round number, not a position — there is no known
    // total, because how many rounds it takes is what is being discovered.
    const round: number | null = readNumber(payload.index);
    return round === null ? base : `${base} (round ${round})`;
  }

  return `${base}${positionSuffix(payload.index, payload.total)}`;
}
